import { ComputedNodeStatus, NodeStatus, OSEnum, Workload } from '@cast/types';
import { KentRebalancingConstraints } from '@cast/types-kent';

import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { RebalanceNode } from './_types';
import { RebalanceNodeEnum, RebalanceNodeWorkload } from '../_types';

const mapRebalanceNodeWorkload = (
  workload: Workload
): RebalanceNodeWorkload => {
  return {
    name: workload.name,
    namespace: workload.namespace,
    resource: workload.resource,
    replicas: workload.replicas,
    issues: workload.issues,
    nodes:
      workload.nodes?.map((node) => ({ id: node.id, name: node.name })) || [],
    status: workload.status!.migrationStatus!,
  };
};

const groupWorkloadsByNode = (
  workloads: Workload[] | undefined
): Record<string, RebalanceNodeWorkload[]> => {
  if (!workloads?.length) {
    return {};
  }

  return workloads.reduce((acc, workload) => {
    const mappedWorkload = mapRebalanceNodeWorkload(workload);
    mappedWorkload.nodes.forEach((node) => {
      if (!(node.name in acc)) {
        acc[node.name] = [];
      }

      acc[node.name].push(mappedWorkload);
    });
    return acc;
  }, {} as Record<string, RebalanceNodeWorkload[]>);
};

export const mapRebalancingNodes = ({
  nodes,
  workloads,
  preSelectedNodeIds,
  isKentRebalancingMode,
}: {
  nodes: NodeWithDependencies[];
  workloads: Workload[] | undefined;
  preSelectedNodeIds: string[];
  isKentRebalancingMode: boolean;
}): RebalanceNode[] => {
  if (!nodes?.length) {
    return [];
  }

  const groupedWorkloads = groupWorkloadsByNode(workloads);

  return nodes.map((node) => {
    let cpu = 0;

    if (node.resources?.cpuCapacityMilli) {
      cpu = node.resources.cpuCapacityMilli / 1000;
    }

    let workloads: RebalanceNodeWorkload[] = [];
    if (node.name && node.name in groupedWorkloads) {
      workloads = groupedWorkloads[node.name];
    }

    const problematicPods = workloads.reduce((sum, workload) => {
      if (workload.status === 'not-ready') {
        sum += 1;
      }
      return sum;
    }, 0);

    const costPerCPU = node.pricingInfo?.totalPrice
      ? +node.pricingInfo.totalPrice / cpu
      : undefined;

    return {
      id: node.id,
      name: node.name,
      instanceType: node.instanceType,
      pricingInfo: node.pricingInfo,
      resourceOffering: node.resourceOffering,
      cpu,
      costPerCPU,
      memoryBytes: node.resources?.memCapacityMib
        ? node.resources.memCapacityMib * Math.pow(1024, 2)
        : undefined,
      problematicPods,
      totalPods: workloads.length,
      // In Kent mode the rebalancing constraints are the single source of truth for readiness
      status: isKentRebalancingMode
        ? node.problems?.length
          ? RebalanceNodeEnum.NOT_READY
          : RebalanceNodeEnum.READY
        : problematicPods || node.isFailedToDrain || !!node.problems?.length
        ? RebalanceNodeEnum.NOT_READY
        : RebalanceNodeEnum.READY,
      workloads,
      operatingSystem: node.nodeInfo?.operatingSystem as OSEnum,
      nodeIssues: node.problems,
      wasPreSelected: !!node.id && preSelectedNodeIds.includes(node.id),
      labels: node.labels,
    };
  });
};

export const isNodeEligibleForRebalancingWizard = ({
  node,
  isKentRebalancingMode,
}: {
  node: NodeWithDependencies;
  isKentRebalancingMode: boolean;
}): boolean => {
  if (isKentRebalancingMode) {
    return (
      node.state.phase === NodeStatus.ready ||
      node.state.phase === ComputedNodeStatus.CORDONED
    );
  }

  return node.state.phase === NodeStatus.ready;
};

export const isNodeRebalanceableInKentMode = ({
  node,
  kentRebalancingConstraints,
}: {
  node: RebalanceNode;
  kentRebalancingConstraints?: KentRebalancingConstraints;
}): boolean => {
  return (kentRebalancingConstraints?.problematicNodes || []).every(
    (problematicNode) => problematicNode.nodeName !== node.name
  );
};
